/**
 * Which header a Shopify Storefront credential authenticates with.
 *
 * Shopify issues two kinds of Storefront credential and treats their headers as
 * distinct auth schemes rather than aliases — a token sent under the other
 * one's header is rejected with `401 UNAUTHORIZED`, not accepted:
 *
 * - `public` — minted by `storefrontAccessTokenCreate`. An unprefixed 32-char
 *   hex string. Cannot be scoped, so it carries every unauthenticated scope the
 *   app holds.
 * - `delegate` — minted by `delegateAccessTokenCreate`. Carries the `shpat_`
 *   prefix of the offline admin token it was delegated from, and only the
 *   scopes named at delegation.
 *
 * Intelligems switched to delegated tokens so the credential in a shopper's
 * browser could be narrowed, which means `organization.storefrontToken` now
 * holds a public token for orgs installed before the switch and a delegate
 * token for every org installed after it. Nothing recorded which is which, so
 * the prefix is the only signal available, and every caller has to agree on how
 * to read it — hence one classifier here rather than a rule per call site.
 */
export type ShopifyStorefrontTokenKind = "public" | "delegate";

/**
 * Prefixes Shopify puts on server-issued access tokens. Delegate tokens are
 * `shpat_`; the rest are the sibling formats from the same 2021 prefix scheme,
 * matched so a credential swapped in by hand is classified rather than silently
 * treated as public. Tokens predating that scheme are unprefixed, which is why
 * "no known prefix" means public rather than unknown.
 *
 * `shpua_` is in the list because Shopify issues it for storefront credentials
 * that authenticate as PRIVATE — verified against a live shop, where the same
 * `shpua_` token answers 401 UNAUTHORIZED under
 * `X-Shopify-Storefront-Access-Token` and 200 under
 * `Shopify-Storefront-Private-Token`. Its absence read as "no known prefix",
 * so the classifier called it public and every Storefront read for such an org
 * 401'd — the failure is total for that org and silent everywhere else, since
 * nothing else about the token looks wrong.
 */
const SHOPIFY_SERVER_TOKEN_PREFIXES = [
  "shpat_",
  "shpca_",
  "shppa_",
  "shpua_",
] as const;

export const SHOPIFY_STOREFRONT_AUTH_HEADERS = {
  /** The public token, sent the way a storefront page authenticates. */
  public: "X-Shopify-Storefront-Access-Token",
  /** The delegated token, sent the way a private/headless client does. */
  delegate: "Shopify-Storefront-Private-Token",
} as const satisfies Record<ShopifyStorefrontTokenKind, string>;

export const classifyShopifyStorefrontToken = (
  token: string
): ShopifyStorefrontTokenKind =>
  SHOPIFY_SERVER_TOKEN_PREFIXES.some((prefix) => token.startsWith(prefix))
    ? "delegate"
    : "public";

/**
 * The auth header for a Storefront token, as a spreadable object.
 *
 * Returning the header rather than its name is deliberate: a caller cannot pair
 * this token with a different header, or that header with a different token,
 * without going out of its way.
 */
export const shopifyStorefrontAuthHeader = (
  token: string
): Record<string, string> => ({
  [SHOPIFY_STOREFRONT_AUTH_HEADERS[classifyShopifyStorefrontToken(token)]]:
    token,
});
